import React, { useState } from 'react';
import { useAlerts } from '../contexts/AlertsContext';
import type { AlertPreferences } from '@financial-portfolio/shared/types';

interface AlertSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AlertSettingsModal: React.FC<AlertSettingsModalProps> = ({ isOpen, onClose }) => {
  const { preferences, updatePreferences } = useAlerts();
  const [localPreferences, setLocalPreferences] = useState<AlertPreferences>(preferences);

  const handleSave = () => {
    updatePreferences(localPreferences);
    onClose();
  };

  const handleCancel = () => {
    setLocalPreferences(preferences); // Reset to original
    onClose();
  };

  const handleGlobalToggle = (enabled: boolean) => {
    setLocalPreferences(prev => ({ ...prev, enabled }));
  };

  const handleCategoryToggle = (category: keyof AlertPreferences['categories'], enabled: boolean) => {
    setLocalPreferences(prev => ({
      ...prev,
      categories: {
        ...prev.categories,
        [category]: {
          ...prev.categories[category],
          enabled,
        },
      },
    }));
  };

  const handleThresholdChange = (
    category: 'property_value' | 'rental_yield' | 'expenses',
    value: number
  ) => {
    setLocalPreferences(prev => ({
      ...prev,
      categories: {
        ...prev.categories,
        [category]: {
          ...prev.categories[category],
          thresholdPercentage: value,
        },
      },
    }));
  };

  const handleCashFlowToggle = (alertOnNegative: boolean) => {
    setLocalPreferences(prev => ({
      ...prev,
      categories: {
        ...prev.categories,
        cash_flow: {
          ...prev.categories.cash_flow,
          alertOnNegative,
        },
      },
    }));
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={handleCancel}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <h2 className="text-2xl font-bold">Alert Settings</h2>
              </div>
              <button
                onClick={handleCancel}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded p-1 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="mt-2 text-blue-100 text-sm">
              Customize how and when you receive portfolio alerts
            </p>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-220px)]">
            {/* Global Toggle */}
            <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <div className="font-semibold text-gray-900 dark:text-gray-100">Enable All Alerts</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Master switch for all portfolio alerts</div>
                </div>
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={localPreferences.enabled}
                    onChange={(e) => handleGlobalToggle(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </div>
              </label>
            </div>

            {/* Category Settings */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Alert Categories</h3>

              {/* Property Value Alerts */}
              <div className="border border-gray-200 rounded-lg p-4">
                <label className="flex items-center justify-between cursor-pointer mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">🏠</span>
                    <div>
                      <div className="font-medium text-gray-900">Property Value Changes</div>
                      <div className="text-sm text-gray-600">Get notified when property values change significantly</div>
                    </div>
                  </div>
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={localPreferences.categories.property_value.enabled}
                      onChange={(e) => handleCategoryToggle('property_value', e.target.checked)}
                      disabled={!localPreferences.enabled}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50"></div>
                  </div>
                </label>
                {localPreferences.categories.property_value.enabled && localPreferences.enabled && (
                  <div className="ml-9">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Alert threshold: {localPreferences.categories.property_value.thresholdPercentage}% change
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="20"
                      step="1"
                      value={localPreferences.categories.property_value.thresholdPercentage}
                      onChange={(e) => handleThresholdChange('property_value', parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>1%</span>
                      <span>20%</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Rental Yield Alerts */}
              <div className="border border-gray-200 rounded-lg p-4">
                <label className="flex items-center justify-between cursor-pointer mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">💰</span>
                    <div>
                      <div className="font-medium text-gray-900">Rental Yield</div>
                      <div className="text-sm text-gray-600">Alert when yield drops below threshold</div>
                    </div>
                  </div>
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={localPreferences.categories.rental_yield.enabled}
                      onChange={(e) => handleCategoryToggle('rental_yield', e.target.checked)}
                      disabled={!localPreferences.enabled}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50"></div>
                  </div>
                </label>
                {localPreferences.categories.rental_yield.enabled && localPreferences.enabled && (
                  <div className="ml-9">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Minimum yield: {localPreferences.categories.rental_yield.thresholdPercentage}%
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="10"
                      step="0.5"
                      value={localPreferences.categories.rental_yield.thresholdPercentage}
                      onChange={(e) => handleThresholdChange('rental_yield', parseFloat(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>1%</span>
                      <span>10%</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Cash Flow Alerts */}
              <div className="border border-gray-200 rounded-lg p-4">
                <label className="flex items-center justify-between cursor-pointer mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">💵</span>
                    <div>
                      <div className="font-medium text-gray-900">Cash Flow</div>
                      <div className="text-sm text-gray-600">Monitor your portfolio cash flow</div>
                    </div>
                  </div>
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={localPreferences.categories.cash_flow.enabled}
                      onChange={(e) => handleCategoryToggle('cash_flow', e.target.checked)}
                      disabled={!localPreferences.enabled}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50"></div>
                  </div>
                </label>
                {localPreferences.categories.cash_flow.enabled && localPreferences.enabled && (
                  <div className="ml-9">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={localPreferences.categories.cash_flow.alertOnNegative}
                        onChange={(e) => handleCashFlowToggle(e.target.checked)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Alert on negative cash flow</span>
                    </label>
                  </div>
                )}
              </div>

              {/* Expenses Alerts */}
              <div className="border border-gray-200 rounded-lg p-4">
                <label className="flex items-center justify-between cursor-pointer mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">📊</span>
                    <div>
                      <div className="font-medium text-gray-900">Expense Changes</div>
                      <div className="text-sm text-gray-600">Alert when expenses increase significantly</div>
                    </div>
                  </div>
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={localPreferences.categories.expenses.enabled}
                      onChange={(e) => handleCategoryToggle('expenses', e.target.checked)}
                      disabled={!localPreferences.enabled}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50"></div>
                  </div>
                </label>
                {localPreferences.categories.expenses.enabled && localPreferences.enabled && (
                  <div className="ml-9">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Alert threshold: {localPreferences.categories.expenses.thresholdPercentage}% increase
                    </label>
                    <input
                      type="range"
                      min="5"
                      max="50"
                      step="5"
                      value={localPreferences.categories.expenses.thresholdPercentage}
                      onChange={(e) => handleThresholdChange('expenses', parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>5%</span>
                      <span>50%</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Market News Alerts */}
              <div className="border border-gray-200 rounded-lg p-4">
                <label className="flex items-center justify-between cursor-pointer">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">📰</span>
                    <div>
                      <div className="font-medium text-gray-900">Market News</div>
                      <div className="text-sm text-gray-600">Important market updates and trends</div>
                    </div>
                  </div>
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={localPreferences.categories.market_news.enabled}
                      onChange={(e) => handleCategoryToggle('market_news', e.target.checked)}
                      disabled={!localPreferences.enabled}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50"></div>
                  </div>
                </label>
              </div>

              {/* General Alerts */}
              <div className="border border-gray-200 rounded-lg p-4">
                <label className="flex items-center justify-between cursor-pointer">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">📢</span>
                    <div>
                      <div className="font-medium text-gray-900">General Alerts</div>
                      <div className="text-sm text-gray-600">System notifications and updates</div>
                    </div>
                  </div>
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={localPreferences.categories.general.enabled}
                      onChange={(e) => handleCategoryToggle('general', e.target.checked)}
                      disabled={!localPreferences.enabled}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50"></div>
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 p-4 bg-gray-50 flex justify-end gap-3">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Save Settings
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
